import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Clock, ArrowRight } from 'lucide-react';
import { API } from '../lib/constants';

const VerifyMessage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, already, expired, error
  const [athleteName, setAthleteName] = useState('');
  const [athleteUsername, setAthleteUsername] = useState('');

  useEffect(() => {
    const verify = async () => {
      try {
        const res = await axios.get(`${API}/api/verify/${token}`);
        if (res.data.already_verified) {
          setStatus('already');
        } else {
          setStatus('success');
        }
        setAthleteName(res.data.athlete_name || '');
        setAthleteUsername(res.data.athlete_username || '');
      } catch (err) {
        if (err.response?.status === 410) {
          setStatus('expired');
        } else if (err.response?.status === 404) {
          setStatus('error');
        } else {
          setStatus('error');
        }
      }
    };
    verify();
  }, [token]);

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="w-14 h-14 bg-[#1a1a1a] rounded-full flex items-center justify-center mx-auto mb-6">
          <span className="text-[#16A34A] font-black text-2xl">O</span>
        </div>

        {status === 'loading' && (
          <div>
            <div className="w-10 h-10 border-2 border-[#16A34A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-white/50">Verifying your identity...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="animate-in fade-in">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-400" />
            </div>
            <h1 className="text-2xl font-black mb-2">Verified!</h1>
            <p className="text-white/50 mb-6">
              Your email has been verified. {athleteName} can now see that your message is authentic.
            </p>
            {athleteUsername && (
              <Button onClick={() => navigate(`/profile/${athleteUsername}`)}
                className="bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold rounded-full px-8">
                View {athleteName}'s Profile <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            )}
          </div>
        )}

        {status === 'already' && (
          <div className="animate-in fade-in">
            <div className="w-16 h-16 bg-[#16A34A]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-[#16A34A]" />
            </div>
            <h1 className="text-2xl font-black mb-2">Already Verified</h1>
            <p className="text-white/50 mb-6">
              Your email was already verified. {athleteName} can see your message is authentic.
            </p>
          </div>
        )}

        {status === 'expired' && (
          <div className="animate-in fade-in">
            <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="h-8 w-8 text-orange-400" />
            </div>
            <h1 className="text-2xl font-black mb-2">Link Expired</h1>
            <p className="text-white/50 mb-6">
              This verification link has expired. Ask the athlete to send a new one.
            </p>
          </div>
        )}

        {status === 'error' && (
          <div className="animate-in fade-in">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-8 w-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-black mb-2">Invalid Link</h1>
            <p className="text-white/50 mb-6">
              This verification link is invalid or has already been used.
            </p>
          </div>
        )}

        <div className="mt-8">
          <button onClick={() => navigate('/')} className="text-white/20 text-sm hover:text-white/40">
            ← Back to Offer
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyMessage;
